import {
  IonButton,
  IonContent,
  IonIcon,
  IonImg,
  IonMenuToggle,
  IonPage,
  useIonToast,
} from '@ionic/react';
import { ellipsisVertical, refreshCircleOutline } from 'ionicons/icons';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { firstValueFrom } from 'rxjs';
import { IState } from 'appRedux/createStore';
import {
  CasinoService,
  ISystemDashboardResponse,
  ISystemDashboardWidget,
  ISystemDashboardFreshnessRow,
} from 'services/casino';
import { CollapsibleDataGrid, GridColumn } from './collapsibleGrid';
import {
  SYSTEM_DASHBOARD_GROUP,
  FreshnessRow,
  formatAgeMinutes,
  formatViewerLocal,
  viewerTimeZone,
  viewerZoneLabel,
  StaleLevel,
} from './dashboardWidgets';

const MenuKebab = () => (
  <div className="system-dashboard__menu-fab">
    <IonMenuToggle menu="main">
      <IonButton fill="clear" className="system-dashboard__menu-btn" aria-label="Open menu">
        <IonIcon icon={ellipsisVertical} className="system-dashboard__menu-icon" />
      </IonButton>
    </IonMenuToggle>
  </div>
);

const ForbiddenCard = () => (
  <IonPage className="app-parent system-dashboard">
    <IonContent className="system-dashboard">
      <MenuKebab />
      <div className="system-dashboard__content">
        <div className="system-dashboard__breadcrumb-row">
          <IonMenuToggle menu="main">
            <IonButton fill="clear" className="system-dashboard__back" aria-label="Open menu">
              <IonImg src="assets/images/back-icon.svg" />
            </IonButton>
          </IonMenuToggle>
          <div className="system-dashboard__breadcrumb">
            <span>System Dashboard</span>
          </div>
        </div>
        <div className="system-dashboard__forbidden-card">
          <h2>403 · System Dashboard not enabled</h2>
          <p>
            Requires Cognito group <strong>{SYSTEM_DASHBOARD_GROUP}</strong>. Ask ops to assign it
            (dev pool and prod <code>us-east-1_wixKjr4v5</code>).
          </p>
        </div>
      </div>
    </IonContent>
  </IonPage>
);

function toRows(raw: ISystemDashboardFreshnessRow[] | undefined): FreshnessRow[] {
  if (!Array.isArray(raw)) return [];
  return raw.map((r) => ({
    casinoId: Number(r.casinoId) || 0,
    casinoName: String(r.casinoName || `Casino ${r.casinoId}`),
    insightInstance: r.insightInstance ?? null,
    collection: r.collection,
    minUpdateDate: r.minUpdateDate ?? null,
    maxUpdateDate: r.maxUpdateDate ?? null,
    currentDateTime: r.currentDateTime ?? null,
    ageMinutes: typeof r.ageMinutes === 'number' ? r.ageMinutes : null,
    staleLevel: (r.staleLevel as StaleLevel) || 'unknown',
    timeZoneLabel: r.timeZoneLabel || 'true-UTC',
    message: r.message ?? null,
  }));
}

function WidgetTable({ widget }: { widget: ISystemDashboardWidget }) {
  const rows = useMemo(() => toRows(widget.rows), [widget.rows]);
  const columns: GridColumn[] = useMemo(() => {
    if (Array.isArray(widget.columns) && widget.columns.length) {
      return widget.columns.map((c) => ({
        key: c.key,
        label: c.label,
        shortLabel: c.shortLabel || c.label.slice(0, 3),
      }));
    }
    // Fallback column set if server omits columns.
    return [
      { key: 'casinoName', label: 'Casino', shortLabel: 'Cas' },
      { key: 'collection', label: 'Collection', shortLabel: 'Col' },
      { key: 'minUpdateDate', label: 'Min updateDate', shortLabel: 'Min' },
      { key: 'maxUpdateDate', label: 'Max updateDate', shortLabel: 'Max' },
      { key: 'currentDateTime', label: 'Server now', shortLabel: 'Now' },
      { key: 'ageMinutes', label: 'Age', shortLabel: 'Age' },
    ].filter((c) => c.key !== 'collection' || widget.id === 'insightFreshness');
  }, [widget.columns, widget.id]);

  const viewZone = viewerZoneLabel();

  return (
    <section className="system-dashboard__card" data-widget={widget.id}>
      <div className="system-dashboard__card-head">
        <h2>{widget.title}</h2>
        <p>
          Display: <strong>{viewZone}</strong> (storage: true UTC; age = now − max)
          {widget.staleThresholds
            ? ` · amber > ${widget.staleThresholds.amberMinutes}m · red > ${widget.staleThresholds.redMinutes}m`
            : null}
          {widget.status === 'error' ? ` · error: ${widget.error || 'failed'}` : null}
        </p>
      </div>
      <CollapsibleDataGrid
        tableId={widget.id}
        columns={columns}
        rows={rows as unknown as Record<string, unknown>[]}
        getRowKey={(row, i) => `${(row as FreshnessRow).casinoId}-${i}`}
        rowClassName={(row) =>
          `system-dashboard__row system-dashboard__row--${(row as FreshnessRow).staleLevel}`
        }
        renderCell={(row, key) => {
          const r = row as unknown as FreshnessRow;
          const tz = viewerTimeZone();
          if (key === 'casinoName') {
            return (
              <>
                <div className="system-dashboard__casino-name">{r.casinoName}</div>
                <div className="system-dashboard__casino-id">#{r.casinoId}</div>
                {r.message ? (
                  <div className="system-dashboard__row-msg">{r.message}</div>
                ) : null}
              </>
            );
          }
          if (key === 'collection') return <code>{r.collection || '—'}</code>;
          if (key === 'minUpdateDate') return formatViewerLocal(r.minUpdateDate, tz);
          if (key === 'maxUpdateDate') return formatViewerLocal(r.maxUpdateDate, tz);
          if (key === 'currentDateTime') return formatViewerLocal(r.currentDateTime, tz);
          if (key === 'ageMinutes') {
            return (
              <span className={`system-dashboard__age system-dashboard__age--${r.staleLevel}`}>
                {formatAgeMinutes(r.ageMinutes)}
              </span>
            );
          }
          const v = (r as unknown as Record<string, unknown>)[key];
          return v == null ? '—' : String(v);
        }}
      />
    </section>
  );
}

export const SystemDashboardPage = () => {
  const casinoSvc = useMemo(() => new CasinoService(), []);
  const [presentToast] = useIonToast();
  const groups =
    (useSelector<IState>((s) => s.app.persistedState.userInfo?.groups) as string[]) || [];
  const canAccess = groups.includes(SYSTEM_DASHBOARD_GROUP);

  const [phase, setPhase] = useState<'idle' | 'loading' | 'ready'>('idle');
  const [refreshBusy, setRefreshBusy] = useState(false);
  const [payload, setPayload] = useState<ISystemDashboardResponse | null>(null);
  const [lastRefreshed, setLastRefreshed] = useState<string | null>(null);
  const loadSeq = useRef(0);
  const presentToastRef = useRef(presentToast);
  presentToastRef.current = presentToast;

  const load = useCallback(
    async (opts?: { fromRefresh?: boolean }) => {
      if (!canAccess) return;
      const seq = ++loadSeq.current;
      if (opts?.fromRefresh) setRefreshBusy(true);
      else setPhase('loading');
      try {
        const res = await firstValueFrom(casinoSvc.GetSystemDashboard());
        if (seq !== loadSeq.current) return;
        setPayload(res && typeof res === 'object' ? res : null);
        setLastRefreshed(new Date().toISOString());
        setPhase('ready');
      } catch (err: any) {
        if (seq !== loadSeq.current) return;
        setPayload(null);
        setPhase('ready');
        const status = err?.response?.status || err?.status;
        if (status !== 403 && status !== 304) {
          presentToastRef
            .current({
              position: 'top',
              duration: 4000,
              color: 'danger',
              message: 'Failed to load System Dashboard.',
            })
            .catch(() => null);
        }
      } finally {
        if (seq === loadSeq.current) setRefreshBusy(false);
      }
    },
    [canAccess, casinoSvc]
  );

  useEffect(() => {
    void load();
  }, [load]);

  if (!canAccess) return <ForbiddenCard />;

  const widgets = Array.isArray(payload?.widgets) ? payload!.widgets! : [];

  return (
    <IonPage className="app-parent system-dashboard">
      <IonContent className="system-dashboard">
        <MenuKebab />
        <div className="system-dashboard__content">
          <div className="system-dashboard__breadcrumb-row">
            <IonMenuToggle menu="main">
              <IonButton fill="clear" className="system-dashboard__back" aria-label="Open menu">
                <IonImg src="assets/images/back-icon.svg" />
              </IonButton>
            </IonMenuToggle>
            <div className="system-dashboard__breadcrumb">
              <span>System Dashboard</span>
            </div>
          </div>

          <div className="system-dashboard__header">
            <div className="system-dashboard__title-row">
              <h1 className="system-dashboard__title">System Dashboard</h1>
              <IonButton
                className="system-dashboard__refresh"
                fill="solid"
                disabled={refreshBusy || phase === 'loading'}
                onClick={() => void load({ fromRefresh: true })}
              >
                <IonIcon icon={refreshCircleOutline} slot="start" />
                {refreshBusy ? 'Refreshing…' : 'Refresh'}
              </IonButton>
            </div>
            <p className="system-dashboard__subtitle">
              Insight and machines freshness. Stored <code>updateDate</code> / server now are{' '}
              <strong>true UTC</strong> (age = now − max; no casino tz). Displayed in your local
              zone (<strong>{viewerZoneLabel()}</strong>).
              {lastRefreshed ? (
                <> Last refreshed: {formatViewerLocal(lastRefreshed)}.</>
              ) : null}
              {payload?.updateDateSemantics ? (
                <> Storage semantics: {payload.updateDateSemantics}.</>
              ) : null}
            </p>
          </div>

          {phase === 'loading' && !payload ? (
            <p className="system-dashboard__loading">Loading…</p>
          ) : (
            widgets.map((w) => <WidgetTable key={w.id} widget={w} />)
          )}
        </div>
      </IonContent>
    </IonPage>
  );
};

export default SystemDashboardPage;
